import Alert from '@mui/material/Alert';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { RadioComponentSwitch } from '@openx/components-core/lib/RadioComponentSwitch/RadioComponentSwitch.tsx';
import { dealFormField, targetingSectionDealFormSwitch } from '@openx/types/deal.ts';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealFormContext } from '../../DealForm.context.tsx';
import { CuratedTargetingPackageTab } from './components/CuratedTargetingPackageTab.tsx';
import { ExistingTargetingPackageTab } from './components/ExistingTargetingPackageTab.tsx';
import { NewTargetingPackageTab } from './components/NewTargetingPackageTab.tsx';

type DealPackageSectionProps = {
  sessionAccountName?: string;
  hideCuratedPackageTab?: boolean;
  currency: string;
};

export const DealPackageSection = (props: DealPackageSectionProps) => {
  const { t } = useTranslation();
  const { pckgIsInitialLoading, pckgInitialLoadFailed } = useDealFormContext();

  const renderContent = () => {
    if (pckgInitialLoadFailed) {
      return (
        <Alert data-test="deal-package-section-error" severity="error">
          {t('Failed to load the targeting package. Please reload the page to edit this deal.')}
        </Alert>
      );
    }

    if (pckgIsInitialLoading) {
      return <Loader data-test="deal-package-section-loader" />;
    }

    return <DealPackageSectionContent {...props} />;
  };

  return (
    <FormSection
      title={t('Targeting Criteria')}
      description={t(
        'deal_form_targeting_description',
        'You can select an existing package, choose custom inventory and targeting criteria, or create a new reusable package.'
      )}
    >
      {renderContent()}
    </FormSection>
  );
};

const DealPackageSectionContent = ({
  sessionAccountName,
  currency,
  hideCuratedPackageTab = false,
}: DealPackageSectionProps) => {
  const { t } = useTranslation();
  const {
    formState: { errors },
  } = useFormContext();

  const { pckg, pckgIsViaInteraction, pckgClear, pckgInitialMode } = useDealFormContext();
  const observablePckgUid = useWatch({ name: dealFormField.PACKAGE_UID });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <test for pckg?.uid is sufficient>
  useEffect(() => {
    if (pckgIsViaInteraction) {
      return;
    }

    /** fallback to Unique Targeting if Curated Package tab is hidden */
    if (pckgInitialMode === targetingSectionDealFormSwitch.CURATED_PACKAGE && hideCuratedPackageTab) {
      setSelectedOption(targetingSectionDealFormSwitch.UNIQUE_TARGETING);
    }

    setSelectedOption(pckgInitialMode);
  }, [observablePckgUid, pckg?.uid, pckgIsViaInteraction]);

  const [selectedOption, setSelectedOption] = useState<undefined | number>();

  /** Case when user selects Curated package on "Existing Package" tab */
  // biome-ignore lint/correctness/useExhaustiveDependencies: <check for pckg.uid is sufficient>
  useEffect(() => {
    if (selectedOption === targetingSectionDealFormSwitch.EXISTING_PACKAGE && pckg && pckg?.is_curated) {
      setSelectedOption(targetingSectionDealFormSwitch.CURATED_PACKAGE);
    }
  }, [pckg?.uid, selectedOption]);

  const onSwitchChange = useCallback(
    (selectedOption: number) => {
      pckgClear(true);
      setSelectedOption(selectedOption);
    },
    [pckgClear]
  );

  const displayValidationError = !isEmpty(errors) && typeof selectedOption === 'undefined';

  const radioSwitchContent = () => {
    const output = [
      {
        child: <NewTargetingPackageTab currency={currency} />,
        dataTest: 'use-unique-targeting-settings-radio',
        helperText: t('Using unique targeting, you create 1-1, custom targeting and a custom package for this deal.'),
        label: t('Use Unique Targeting Settings'),
      },
      {
        child: <ExistingTargetingPackageTab currency={currency} />,
        dataTest: 'use-existing-targeting-package-radio',
        helperText: t(
          'Choose from a broad list of reusable inventory targeting contained in packages created by users.'
        ),
        label: t('Use An Existing Package'),
      },
    ];

    if (!hideCuratedPackageTab) {
      output.push({
        child: <CuratedTargetingPackageTab currency={currency} sessionAccountName={sessionAccountName} />,
        dataTest: 'use-curated-targeting-package-radio',
        helperText: t(
          'Select from a list of expertly curated packages that fill a variety of common targeting use cases.'
        ),
        label: t('Use Curated Package'),
      });
    }

    return output;
  };

  return (
    <>
      <RadioComponentSwitch divider onSwitchChange={onSwitchChange} selectedOption={selectedOption}>
        {radioSwitchContent()}
      </RadioComponentSwitch>

      {displayValidationError && (
        <Alert data-test="targeting-selection-alert" severity="error">
          {t('Targeting selection is required')}
        </Alert>
      )}
    </>
  );
};
